import { TimeSlot } from '@wix/ambassador-bookings-availability-v2-time-slot/types';
import { Service } from '@wix/ambassador-bookings-services-v2-service/types';
import {
  formatRfcTimeStringToTimeSlotView,
  fromLocalDateTimeToRfc,
} from '@wix/bookings-catalog-calendar-viewer-utils/dateAndTime';
import { getDurationText } from '@wix/bookings-catalog-calendar-viewer-utils/duration';
import {
  AsyncComponentDataStatus,
  ViewModelFactoryArgs,
} from '../../../state/types';
import {
  getFormattedCurrency,
  getPaymentDescription,
  isServicePendingApprovalFlow,
} from '@wix/bookings-calendar-catalog-viewer-mapper';
import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import {
  isSlotLocked,
  isSlotWithBookingsPolicyViolation,
  isFullSlot,
  isTooEarlyToBookSlot,
  isTooLateToBookSlot,
  isSlotNoBookFlow,
  isSlotWithOpenWaitingList,
  isSlotReserved,
} from '../../../../../utils/slotAvailability/slotAvailability';
import settingsParams from '../../../settingsParams';
import { LanguageSettings } from '@wix/bookings-catalog-calendar-viewer-utils';
import {
  editor3DummyServiceImages,
  editorDummyServiceImages,
  type ServiceIndex,
  studioDummyServiceImages,
} from '../types';

export type AgendaItemViewModel = {
  serviceName?: string;
  serviceImage?: { src: string; alt?: string };
  startTime?: string;
  duration?: string;
  staffName?: string;
  staffImage?: string;
  location?: string;
  spots?: string;
  price?: string;
  buttonText?: string;
  disabled?: boolean;
  slotId: string;
};

export const createAgendaItemViewModel = ({
  slot,
  state: { languageSettings, services, staffMembers, bookedSlots },
  flowAPI,
}: {
  slot: TimeSlot;
} & ViewModelFactoryArgs): AgendaItemViewModel => {
  const {
    settings,
    translations: { t },
  } = flowAPI;
  const service = (services.data as Record<string, Service>)[slot.serviceId!];

  const staffMemberId = slot.availableResources?.[0]?.resources?.[0].id;
  const staffMemberName = slot.availableResources?.[0]?.resources?.[0].name;
  const staffMember =
    staffMemberId && staffMembers.status === AsyncComponentDataStatus.IDLE
      ? staffMembers.data[staffMemberId]
      : undefined;

  const rfcStartTime = fromLocalDateTimeToRfc(slot.localStartDate!);
  const rfcEndTime = fromLocalDateTimeToRfc(slot.localEndDate!);
  const location = service?.locations?.find(
    ({ id }) => slot.location?.id === id,
  )?.business?.name;

  const formattedTime = formatRfcTimeStringToTimeSlotView(
    rfcStartTime,
    languageSettings.dateRegionalSettingsLocale,
  );

  const serviceName = service?.name!;

  const duration = getDurationText({
    dateRegionalSettingsLocale: languageSettings.dateRegionalSettingsLocale,
    rfcStartTime,
    rfcEndTime,
    t,
  }).durationText;

  const price = getSlotPrice({
    service: service!,
    regionalSettingsLocale: languageSettings.regionalSettingsLocale!,
    t,
  });

  const isAlreadyBooked =
    bookedSlots.status === AsyncComponentDataStatus.IDLE &&
    bookedSlots.data.find((id) => id === slot.eventInfo?.eventId);

  const spotsLeft = getSpotsLeft({ slot, flowAPI });
  let buttonText: string;
  let disabled: boolean = false;
  const policyViolation = getPolicyViolationText({
    flowAPI,
    slot,
  });

  if (isAlreadyBooked) {
    buttonText = settings.get(settingsParams.alreadyBookedButton);
    disabled = true;
  } else if (policyViolation) {
    buttonText = policyViolation;
    disabled = !isSlotNoBookFlow(slot);
  } else if (isServicePendingApprovalFlow(service!)) {
    buttonText = settings.get(settingsParams.pendingApprovalButton);
  } else if (isSlotWithOpenWaitingList(slot)) {
    buttonText = settings.get(settingsParams.joinWaitlistButton);
  } else if (isFullSlot(slot) || isSlotReserved(slot)) {
    buttonText = settings.get(settingsParams.noSpotsLeft);
    disabled = true;
  } else {
    buttonText = settings.get(settingsParams.bookButtonText);
  }

  return {
    slotId: slot.eventInfo?.eventId!,
    startTime: formattedTime,
    serviceName,
    location,
    spots: spotsLeft,
    duration,
    price,
    staffName: staffMember?.name ?? staffMemberName ?? '',
    buttonText,
    disabled,
    serviceImage: service?.media?.mainMedia?.image
      ? {
          src: service?.media?.mainMedia?.image?.url!,
          alt: service?.media?.mainMedia?.image?.altText!,
        }
      : undefined,
    staffImage: staffMember?.mainMedia?.image?.url,
  };
};

const getSpotsLeft = ({
  slot,
  flowAPI: {
    settings,
    translations: { t },
  },
}: {
  slot: TimeSlot;
  flowAPI: ControllerFlowAPI;
}) => {
  const openSpots = slot.remainingCapacity!;

  if (isSlotWithBookingsPolicyViolation(slot)) {
    return;
  }

  if (isSlotLocked(slot)) {
    return;
  }

  if (isFullSlot(slot)) {
    return;
  }

  return t('app.agenda-slot.spots-left.label', {
    openSpots,
  });
};

const getSlotPrice = ({
  t,
  service,
  regionalSettingsLocale,
}: {
  service: Service;
  regionalSettingsLocale: string;
  t: any;
}): string => {
  const payment = service.payment!;

  const getPaymentDescriptionArgs: Parameters<typeof getPaymentDescription>[0] =
    {
      payment,
      regionalSettingsLocale,
      paymentFormatters: {
        variedPrice: ({ minPrice }) =>
          t('payment.from-price', { price: minPrice }),
      },
    };

  return getPaymentDescription(getPaymentDescriptionArgs);
};

const getPolicyViolationText = ({
  flowAPI: { settings },
  slot,
}: {
  flowAPI: ControllerFlowAPI;
  slot: TimeSlot;
}): string | undefined => {
  if (isSlotNoBookFlow(slot)) {
    return settings.get(settingsParams.bookOnlineDisabledIndication);
  }

  if (isTooEarlyToBookSlot(slot)) {
    return settings.get(settingsParams.toEarlyToBookIndication);
  }

  if (isTooLateToBookSlot(slot)) {
    return settings.get(settingsParams.toLateToBookIndication);
  }
};

export const createDummyAgendaItemViewModel = ({
  flowAPI,
  languageSettings,
  overrideStartTime,
  serviceIndex,
}: {
  flowAPI: ControllerFlowAPI;
  languageSettings: LanguageSettings;
  overrideStartTime?: string;
  serviceIndex: ServiceIndex;
}): AgendaItemViewModel => {
  const {
    settings,
    translations: { t },
  } = flowAPI;
  const rfcStartTime = overrideStartTime || '2025-04-07T08:00:00';
  const rfcEndTime = new Date(
    new Date(rfcStartTime).getTime() + 60 * 60 * 1000,
  ).toISOString();

  const duration = getDurationText({
    dateRegionalSettingsLocale: languageSettings.dateRegionalSettingsLocale,
    rfcStartTime,
    rfcEndTime,
    t,
  }).durationText;

  const price = getFormattedCurrency({
    price: 100,
    locale: languageSettings.regionalSettingsLocale,
    currency: 'USD',
  });

  const isWixStudio =
    flowAPI.controllerConfig.wixCodeApi?.location?.query?.dsOrigin === 'studio';

  const isEditor3 =
    flowAPI.controllerConfig.wixCodeApi?.location?.query?.dsOrigin ===
    'odeditor';

  const serviceImage = isWixStudio
    ? studioDummyServiceImages[serviceIndex]
    : isEditor3
    ? editor3DummyServiceImages[serviceIndex]
    : editorDummyServiceImages[serviceIndex];

  return {
    serviceName: t('daily-agenda.dummy.service-name'),
    serviceImage: {
      src: serviceImage,
    },
    startTime: formatRfcTimeStringToTimeSlotView(
      rfcStartTime,
      languageSettings.dateRegionalSettingsLocale,
    ),
    duration,
    staffName: t('daily-agenda.dummy.staff-name'),
    location: t('daily-agenda.dummy.location'),
    spots: getSpotsLeft({ slot: { remainingCapacity: 3 }, flowAPI }),
    price,
    buttonText: settings.get(settingsParams.bookButtonText),
    disabled: false,
    slotId: Math.random().toString(10),
  };
};
